import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { CompanySubscription } from '../../../shared/models';
import { CompanySubscriptionActionButton } from './CompanySubscriptionActionButton';
import { CerebroLink, CerebroText } from '../../../shared/components';
import { SuccessFeeTierTableDisplay } from '../SuccessFeeTierTableDisplay';
import { DateUtils, EnumUtils, FileUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-duotone-svg-icons';

interface CompanySubscriptionDisplayProps {
	companyGuid: string | undefined;
	subscription: CompanySubscription;
	isClosedFunded: boolean;
	onEdit: (subscription: CompanySubscription) => void;
	onEditSuccessTiers: (subscription: CompanySubscription) => void;
	onUploadAgreement: (subscription: CompanySubscription) => void;
	onEditPaymentTerms: (subscription: CompanySubscription) => void;
	onDelete: (subscription: CompanySubscription) => void;
}

export const CompanySubscriptionDisplay: React.FC<CompanySubscriptionDisplayProps> = ({
	companyGuid,
	subscription,
	isClosedFunded,
	onEdit,
	onEditSuccessTiers,
	onUploadAgreement,
	onEditPaymentTerms,
	onDelete,
}) => {
	const showCompanyName = subscription.companyGuid !== companyGuid;

	return (
		<TableContainer sx={{ marginBottom: 2 }}>
			<Table>
				<TableHead>
					<TableRow>
						{showCompanyName ? (
							<TableCell colSpan={6}>Company Subscription Details - {subscription.companyName}</TableCell>
						) : (
							<TableCell colSpan={6}>Company Subscription Details</TableCell>
						)}
					</TableRow>
					<TableRow>
						<TableCell>Status</TableCell>
						<TableCell>Account Manager</TableCell>
						<TableCell>Term</TableCell>
						<TableCell>Subscription Fee</TableCell>
						<TableCell>Success Fees</TableCell>
						{!isClosedFunded && <TableCell></TableCell>}
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell>
							{EnumUtils.GetCompanySubscriptionStatusDescription(subscription.dealStatus)}
						</TableCell>
						<TableCell>{subscription.accountManager}</TableCell>
						<TableCell>
							{subscription.autoRenew && (
								<FontAwesomeIcon icon={faArrowsRotate} title="Auto Renews" size="sm" />
							)}
							{subscription.term}
						</TableCell>
						<TableCell>
							{NumberUtils.FormatCurrency(subscription.subscriptionFee)}{' '}
							{EnumUtils.GetSubscriptionFrequencyDescription(subscription.billingFrequency)}{' '}
						</TableCell>
						<TableCell>
							{subscription.successFeeType == 'Tiered' &&
							subscription.successFeeTiers &&
							subscription.successFeeTiers.length > 0 ? (
								<SuccessFeeTierTableDisplay tiers={subscription.successFeeTiers} />
							) : subscription.successFeeType == 'Flat' ? (
								<CerebroText>
									<strong>Success Fee: </strong> {subscription.basisPoints} bps
								</CerebroText>
							) : (
								<>--</>
							)}
							{subscription.minimumSuccessFee && subscription.minimumSuccessFee > 0 ? (
								<CerebroText>
									<strong>Minimum Success Fee: </strong>{' '}
									{NumberUtils.FormatCurrency(subscription.minimumSuccessFee)}{' '}
								</CerebroText>
							) : (
								<></>
							)}
							{subscription.successFeeType != 'None' && (
								<>
									{subscription.refinanceBenefitType == 'IncrementAmountOnly' && (
										<CerebroText>Refinance Terms: Incremental Financing Amount Only</CerebroText>
									)}
									{subscription.refinanceBenefitType == 'PercentDiscount' && (
										<>
											{subscription.refinanceSuccessFeeDiscount &&
												subscription.refinanceSuccessFeeDiscount > 0 && (
													<CerebroText>
														<strong>Refinance Success Fee Discount: </strong>{' '}
														{subscription.refinanceSuccessFeeDiscount}
														{'%'}
													</CerebroText>
												)}
										</>
									)}
								</>
							)}
						</TableCell>
						{!isClosedFunded && (
							<TableCell>
								<CompanySubscriptionActionButton
									companyGuid={companyGuid}
									subscription={subscription}
									onEdit={onEdit}
									onEditSuccessTiers={onEditSuccessTiers}
									onUploadAgreement={onUploadAgreement}
									onEditPaymentTerms={onEditPaymentTerms}
									onDelete={onDelete}
								/>
							</TableCell>
						)}
					</TableRow>
					{subscription.agreementFileName && (
						<TableRow>
							<TableCell colSpan={6}>
								<CerebroText>
									<CerebroLink
										variant="link"
										target="_blank"
										rel="noreferrer"
										href={CerebroApiEndpoints.Company.SpecificSubscriptionDownload(
											companyGuid,
											subscription.guid
										)}
									>
										{FileUtils.getFileTypeIcon(subscription.agreementFileName)}{' '}
										{subscription.agreementFileName}
									</CerebroLink>{' '}
									was signed by {subscription.agreementSignedBy} on{' '}
									{DateUtils.DisplayDateOnly(subscription.agreementSignedDate?.toString())}
								</CerebroText>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
